import React, { useEffect, useState, useRef } from 'react';
import { X, ChevronDown, ChevronRight } from 'lucide-react';
import { SectionConfig, FieldMapping, PicklistOption, DataType } from '../../types/metadata';
interface FormPreviewProps {
  isOpen: boolean;
  onClose: () => void;
  sections: SectionConfig[];
  fields: FieldMapping[];
  picklistOptions: PicklistOption[];
}
export function FormPreview({
  isOpen,
  onClose,
  sections,
  fields,
  picklistOptions
}: FormPreviewProps) {
  const [mode, setMode] = useState<'wizard' | 'full'>('wizard');
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [isJumpDropdownOpen, setIsJumpDropdownOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const dropdownRef = useRef<HTMLDivElement>(null);
  const activeSections = sections.filter(s => s.active).sort((a, b) => a.order - b.order);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsJumpDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  if (!isOpen) return null;
  const currentSection = activeSections[currentSectionIndex];
  const getFieldsForSection = (sectionKey: string) => {
    return fields.filter(f => f.section === sectionKey && f.active).sort((a, b) => a.positionInSection - b.positionInSection);
  };
  const toggleSection = (sectionKey: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionKey)) {
      newExpanded.delete(sectionKey);
    } else {
      newExpanded.add(sectionKey);
    }
    setExpandedSections(newExpanded);
  };
  const handleJumpToSection = (index: number) => {
    setCurrentSectionIndex(index);
    setIsJumpDropdownOpen(false);
    if (mode === 'full') {
      const sectionKey = activeSections[index].sectionKey;
      setExpandedSections(prev => new Set(prev).add(sectionKey));
    }
  };
  const renderField = (field: FieldMapping) => {
    const isRequired = field.required || field.isRequiredForCompletion;
    const isPreferred = field.preferably;
    const isConditional = !!field.conditionKey;
    return <div key={field.id} className={`mb-5 ${isConditional ? 'border-l-2 border-amber-300 pl-3 py-1' : ''}`} data-id="element-268">
        <div className="flex items-center gap-1.5 mb-1.5" data-id="element-269">
          <label className="text-sm font-medium text-blue-900" data-id="element-270">
            {field.promptText}:
          </label>
          {isRequired && <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" title="Required" data-id="element-271" />}
          {!isRequired && isPreferred && <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" title="Preferred" data-id="element-272" />}
          {isConditional && <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded" data-id="element-273">
              Conditional
            </span>}
        </div>

        <div data-id="element-274">
          {(field.dataType === DataType.Text || field.dataType === DataType.Email || field.dataType === DataType.Phone) && <input type="text" className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-gray-800 text-sm" data-id="element-275" />}

          {field.dataType === DataType.Currency && <div className="relative" data-id="element-276">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-277">
                <span className="text-gray-500 text-sm" data-id="element-278">$</span>
              </div>
              <input type="text" className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-gray-800 text-sm" data-id="element-279" />
            </div>}

          {field.dataType === DataType.Date && <input type="date" className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-gray-800 text-sm" data-id="element-280" />}

          {field.dataType === DataType.YesNo && <div className="flex gap-3" data-id="element-281">
              <button className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 bg-white text-sm transition-colors" data-id="element-282">
                Yes
              </button>
              <button className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 bg-white text-sm transition-colors" data-id="element-283">
                No
              </button>
            </div>}

          {field.dataType === DataType.Picklist && <select className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-gray-800 bg-white text-sm" data-id="element-284">
              <option value="" data-id="element-285">Select...</option>
              {picklistOptions.filter(p => p.questionKey === field.questionKey && p.active).sort((a, b) => a.order - b.order).map(opt => <option key={opt.id} value={opt.optionValue} data-id="element-286">
                    {opt.optionLabel}
                  </option>)}
            </select>}
        </div>
      </div>;
  };
  const renderMissingFields = () => {
    const requiredFields: string[] = [];
    const recommendedFields: string[] = [];
    activeSections.forEach(section => {
      const sectionFields = getFieldsForSection(section.sectionKey);
      sectionFields.forEach(field => {
        if (field.required || field.isRequiredForCompletion) {
          requiredFields.push(`${section.sectionLabel} - ${field.promptText}?`);
        } else if (field.preferably) {
          recommendedFields.push(`${section.sectionLabel} - ${field.promptText}?`);
        }
      });
    });
    return <div className="mt-8 space-y-4" data-id="element-287">
        {requiredFields.length > 0 && <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-id="element-288">
            <h4 className="text-red-700 font-bold text-sm mb-2" data-id="element-289">
              Missing Required Fields
            </h4>
            <ul className="text-red-600 text-sm space-y-0.5" data-id="element-290">
              {requiredFields.map((f, i) => <li key={i} className="pl-4" data-id="element-291">
                  {f}
                </li>)}
            </ul>
          </div>}
        {recommendedFields.length > 0 && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-292">
            <h4 className="text-gray-700 font-bold text-sm mb-2" data-id="element-293">
              Missing Recommended Fields
            </h4>
            <ul className="text-blue-600 text-sm space-y-0.5" data-id="element-294">
              {recommendedFields.map((f, i) => <li key={i} className="pl-4" data-id="element-295">
                  {f}
                </li>)}
            </ul>
          </div>}
      </div>;
  };
  const renderWizardContent = () => {
    if (!currentSection) return null;
    return <div className="flex flex-col h-full" data-id="element-296">
        <div className="flex-1" data-id="element-297">
          <h3 className="text-lg font-medium text-blue-900 mb-6" data-id="element-298">
            {currentSection.sectionLabel}
          </h3>
          <div className="max-w-3xl" data-id="element-299">
            {getFieldsForSection(currentSection.sectionKey).map(renderField)}
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200" data-id="element-300">
            <label className="block text-sm font-bold text-blue-900 mb-2" data-id="element-301">
              Section note (optional)
            </label>
            <textarea className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-700 text-sm h-20 resize-none" placeholder="Capture any additional context for this section..." data-id="element-302" />
            <button className="mt-3 px-4 py-1.5 rounded-full border border-blue-600 text-blue-600 text-sm font-medium hover:bg-blue-50 transition-colors" data-id="element-303">
              Add note
            </button>
          </div>
        </div>

        <div className="mt-8 pt-4 border-t border-gray-200 flex justify-between items-center" data-id="element-304">
          <button onClick={() => setCurrentSectionIndex(Math.max(0, currentSectionIndex - 1))} disabled={currentSectionIndex === 0} className="px-6 py-2 rounded-full border border-gray-300 text-gray-600 text-sm font-medium hover:bg-gray-50 disabled:opacity-40 transition-colors bg-gray-50" data-id="element-305">
            Back
          </button>
          <button onClick={() => setCurrentSectionIndex(Math.min(activeSections.length - 1, currentSectionIndex + 1))} disabled={currentSectionIndex === activeSections.length - 1} className="px-6 py-2 rounded-full bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40 transition-colors" data-id="element-306">
            Next
          </button>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-200 flex items-center text-blue-900 cursor-pointer hover:text-blue-700 transition-colors" data-id="element-307">
          <ChevronRight className="w-5 h-5 mr-1" data-id="element-308" />
          <span className="font-medium" data-id="element-309">Enhanced Notes Preview</span>
        </div>
      </div>;
  };
  const renderFullFormContent = () => {
    return <div data-id="element-310">
        <h3 className="text-gray-700 mb-6 font-medium" data-id="element-311">Full 1003 Form (v1)</h3>
        <div className="divide-y divide-gray-200" data-id="element-312">
          {activeSections.map(section => {
          const isExpanded = expandedSections.has(section.sectionKey);
          return <div key={section.id} data-id="element-313">
                <button onClick={() => toggleSection(section.sectionKey)} className="w-full flex items-center py-4 text-left text-blue-900 hover:bg-gray-50 px-2 rounded-md transition-colors" data-id="element-314">
                  <ChevronRight className={`w-5 h-5 mr-2 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} data-id="element-315" />
                  <span className="text-base font-medium" data-id="element-316">
                    {section.sectionLabel}
                  </span>
                </button>
                {isExpanded && <div className="pl-9 pr-4 pb-6 pt-2 max-w-3xl" data-id="element-317">
                    {getFieldsForSection(section.sectionKey).map(renderField)}
                  </div>}
              </div>;
        })}
        </div>

        {renderMissingFields()}

        <div className="mt-8 flex justify-end" data-id="element-318">
          <button className="px-6 py-2 rounded-full bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors" data-id="element-319">
            Save
          </button>
        </div>
      </div>;
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4 sm:p-8" data-id="element-320">
      <div className="bg-white w-full max-w-5xl h-full max-h-[92vh] rounded-xl shadow-2xl flex flex-col overflow-hidden relative" data-id="element-321">
        {/* Close Button */}
        <button onClick={onClose} className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors z-20" aria-label="Close preview" data-id="element-322">
          <X className="w-5 h-5" data-id="element-323" />
        </button>

        {/* Header */}
        <div className="pt-5 pb-4 text-center relative flex-shrink-0" data-id="element-324">
          <h2 className="text-xl text-slate-500 font-normal tracking-wide" data-id="element-325">
            LO Easy 1003 First Call Capture
          </h2>
          <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-blue-400 via-blue-500 to-blue-400" data-id="element-326" />
        </div>

        {/* Toolbar */}
        <div className="px-6 sm:px-8 py-4 flex justify-between items-center flex-shrink-0" data-id="element-327">
          <div className="font-bold text-gray-700 text-sm" data-id="element-328">
            {mode === 'wizard' ? 'Script Wizard' : 'Full Form'}
          </div>
          <div className="flex gap-2" data-id="element-329">
            {/* Jump Dropdown */}
            <div className="relative" ref={dropdownRef} data-id="element-330">
              <button onClick={() => setIsJumpDropdownOpen(!isJumpDropdownOpen)} className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-blue-600 text-blue-600 text-sm font-medium hover:bg-blue-50 transition-colors" data-id="element-331">
                Jump to 1003 Section
                <ChevronDown className="w-3.5 h-3.5" data-id="element-332" />
              </button>
              {isJumpDropdownOpen && <div className="absolute top-full right-0 mt-1 w-60 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-30 max-h-80 overflow-y-auto" data-id="element-333">
                  {activeSections.map((section, idx) => <button key={section.id} onClick={() => handleJumpToSection(idx)} className={`w-full text-left px-4 py-2 text-sm transition-colors ${currentSectionIndex === idx ? 'bg-blue-100 text-blue-900 font-medium' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-334">
                      {section.sectionLabel}
                    </button>)}
                </div>}
            </div>

            {/* Mode Toggle */}
            <button onClick={() => setMode(mode === 'wizard' ? 'full' : 'wizard')} className="px-4 py-1.5 rounded-full border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors" data-id="element-335">
              {mode === 'wizard' ? 'Full Form' : 'Wizard'}
            </button>

            {/* Pause (decorative) */}
            <button className="px-4 py-1.5 rounded-full border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors" data-id="element-336">
              Pause
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="px-8 sm:px-16 pb-4 flex-shrink-0" data-id="element-337">
          <div className="relative flex items-center" data-id="element-338">
            {/* Background line */}
            <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[2px] bg-gray-300" data-id="element-339" />
            {/* Completed line */}
            {activeSections.length > 1 && <div className="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] bg-gray-500 transition-all duration-300" style={{
            width: `${currentSectionIndex / (activeSections.length - 1) * 100}%`
          }} data-id="element-340" />}
            {/* Dots */}
            <div className="flex justify-between w-full relative z-10" data-id="element-341">
              {activeSections.map((_, idx) => {
              const isPast = idx < currentSectionIndex;
              const isCurrent = idx === currentSectionIndex;
              return <button key={idx} onClick={() => setCurrentSectionIndex(idx)} className={`rounded-full transition-all duration-200 ${isCurrent ? 'w-4 h-4 border-[3px] border-blue-600 bg-white shadow-sm' : isPast ? 'w-3 h-3 bg-gray-500 border-2 border-gray-500' : 'w-3 h-3 bg-gray-300 border-2 border-gray-300'}`} title={activeSections[idx]?.sectionLabel} data-id="element-342" />;
            })}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto px-6 sm:px-8 pb-8" data-id="element-343">
          <div className="max-w-4xl mx-auto border border-gray-300 rounded-lg p-6" data-id="element-344">
            {mode === 'wizard' ? renderWizardContent() : renderFullFormContent()}
          </div>
        </div>
      </div>
    </div>;
}